import type { createClient } from "./supabase/client";

type BrowserSupabaseClient = ReturnType<typeof createClient>;

export interface PersistedOnboarding {
  version: number;
  completedAt: number | null;
}

export const ONBOARDING_VERSION_METADATA_KEY = "pauta_onboarding_version";
export const ONBOARDING_COMPLETED_AT_METADATA_KEY =
  "pauta_onboarding_completed_at";

export function readOnboardingMetadata(metadata: unknown): PersistedOnboarding {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return { version: 0, completedAt: null };
  }

  const values = metadata as Record<string, unknown>;
  const rawVersion = values[ONBOARDING_VERSION_METADATA_KEY];
  const rawCompletedAt = values[ONBOARDING_COMPLETED_AT_METADATA_KEY];
  const parsedCompletedAt =
    typeof rawCompletedAt === "string" ? Date.parse(rawCompletedAt) : Number.NaN;

  return {
    version:
      typeof rawVersion === "number" &&
      Number.isInteger(rawVersion) &&
      rawVersion >= 0
        ? rawVersion
        : 0,
    completedAt: Number.isFinite(parsedCompletedAt) ? parsedCompletedAt : null,
  };
}

/**
 * Store the tour as a user preference in Supabase Auth metadata. This keeps the
 * version cross-device without coupling a UI preference to the profiles schema.
 * The returned Auth user is the canonical value used to update local state.
 */
export async function persistOnboardingCompletion(
  sb: BrowserSupabaseClient,
  currentUserId: string,
  version: number,
  completedAt = new Date().toISOString()
): Promise<PersistedOnboarding | null> {
  if (
    !currentUserId ||
    !Number.isInteger(version) ||
    version <= 0 ||
    !Number.isFinite(Date.parse(completedAt))
  ) {
    return null;
  }

  const current = await sb.auth.getUser();
  if (current.error || current.data.user?.id !== currentUserId) return null;

  const existing = readOnboardingMetadata(current.data.user.user_metadata);
  if (existing.version >= version) return existing;

  const updated = await sb.auth.updateUser({
    data: {
      [ONBOARDING_VERSION_METADATA_KEY]: version,
      [ONBOARDING_COMPLETED_AT_METADATA_KEY]: completedAt,
    },
  });
  if (updated.error || updated.data.user?.id !== currentUserId) return null;

  const persisted = readOnboardingMetadata(updated.data.user.user_metadata);
  return persisted.version >= version ? persisted : null;
}
